import type { ContentType, IdeaCandidate, ThemeCandidate, Weekday } from "./types";

export type MonthlyCommitment = {
  stories: number;
  posts: number;
  reels: number;
};

export type CadenceRequirement = {
  weekday: Weekday;
  contentType: ContentType;
  quantity: number;
};

export type ExpectedSlot = {
  id: string;
  contentType: ContentType;
  scheduledDate?: string;
  ideaId?: string;
  state: "active" | "deleted";
  source: "cadence" | "commitment_remainder" | "manual";
  cadenceDeviation?: {
    expectedWeekday?: Weekday;
    note: string;
  };
};

export type WorkspaceWeek = {
  id: string;
  week: number;
  startsOn: string;
  endsOn: string;
  themeId?: string;
  themeIds?: string[];
  holidayLabels: string[];
};

export type WorkspaceV3Week = Omit<WorkspaceWeek, "themeId" | "themeIds"> & {
  themeIds: string[];
};

export type WorkspaceV2Theme = ThemeCandidate;
export type CommitmentShortfallAcceptance = {
  countFingerprint: string;
  commitment: MonthlyCommitment;
  planned: MonthlyCommitment;
  reason: string;
  actorId: string;
  acceptedAt: number;
};

export type WorkspaceV2Idea = IdeaCandidate & {
  sourceContentPieceId?: string;
  sourceRevisionEventId?: string;
  contentRevision?: number;
  sentContentRevision?: number;
  sentSlotOrder?: number;
  pendingProductionChangedAt?: number;
  pendingProductionChangedBy?: string;
  pendingProductionRationale?: string;
  // Legacy persisted compatibility only. New removals use the direct confirmation actions.
  pendingRemoval?: boolean;
  pendingRemovalRequestedAt?: number;
  pendingRemovalRequestedBy?: string;
  pendingRemovalRationale?: string;
  pendingRemovalSupersededClassification?: "creative_change" | "schedule_only" | "binding_unknown";
  destinationRevision?: number;
  basicIdeaRevision?: number;
};

export type WorkspaceV2 = {
  commitment: MonthlyCommitment;
  cadence: CadenceRequirement[];
  skippedCadenceNotes: string[];
  themes: WorkspaceV2Theme[];
  ideas: WorkspaceV2Idea[];
  weeks: WorkspaceWeek[];
  expectedSlots: ExpectedSlot[];
  finalizedAt?: number;
  commitmentShortfallAcceptance?: CommitmentShortfallAcceptance;
};

export type WorkspaceV3 = Omit<WorkspaceV2, "weeks"> & {
  weeks: WorkspaceV3Week[];
};

export function themeIdsForWeek(week: Pick<WorkspaceWeek, "themeId" | "themeIds">): string[] {
  return [...new Set([...(week.themeIds ?? []), ...(week.themeId ? [week.themeId] : [])])];
}

export function weekHasTheme(week: Pick<WorkspaceWeek, "themeId" | "themeIds">) {
  return themeIdsForWeek(week).length > 0;
}

export const emptyCommitment: MonthlyCommitment = { stories: 0, posts: 0, reels: 0 };

export function commitmentForType(commitment: MonthlyCommitment, contentType: ContentType) {
  return contentType === "story" ? commitment.stories : contentType === "post" ? commitment.posts : commitment.reels;
}

export function commitmentKeyForType(contentType: ContentType): keyof MonthlyCommitment {
  return contentType === "story" ? "stories" : contentType === "post" ? "posts" : "reels";
}
